import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { IdCard, Save, Loader2 } from 'lucide-react';
import { updateDoctor, type DoctorDTO } from '@/services/doctors';
import { formatCpf, formatPhone, stripMask } from '@/utils/masks';
import { BRAZIL_UF_OPTIONS } from '@/lib/doctorOnboarding';
import { toast } from 'sonner';

interface DoctorIdentitySettingsProps {
  doctor: DoctorDTO;
  onUpdate: (updatedDoctor: DoctorDTO) => void;
}

function toDateInputValue(value?: string | null): string {
  if (!value) return '';
  return String(value).slice(0, 10);
}

export function DoctorIdentitySettings({ doctor, onUpdate }: DoctorIdentitySettingsProps) {
  const [cpf, setCpf] = useState(formatCpf(doctor.cpf || '') || '');
  const [dateOfBirth, setDateOfBirth] = useState(toDateInputValue(doctor.dateOfBirth));
  const [sex, setSex] = useState<'M' | 'F' | ''>((doctor.sex as 'M' | 'F' | undefined) || '');
  const [phone, setPhone] = useState(formatPhone(doctor.phone || '') || '');
  const [officeStreet, setOfficeStreet] = useState(
    doctor.officeStreet || doctor.clinicAddress || '',
  );
  const [officeCity, setOfficeCity] = useState(doctor.officeCity || '');
  const [officeState, setOfficeState] = useState(doctor.officeState || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setCpf(formatCpf(doctor.cpf || '') || '');
    setDateOfBirth(toDateInputValue(doctor.dateOfBirth));
    setSex((doctor.sex as 'M' | 'F' | undefined) || '');
    setPhone(formatPhone(doctor.phone || '') || '');
    setOfficeStreet(doctor.officeStreet || doctor.clinicAddress || '');
    setOfficeCity(doctor.officeCity || '');
    setOfficeState(doctor.officeState || '');
  }, [
    doctor.id,
    doctor.cpf,
    doctor.dateOfBirth,
    doctor.sex,
    doctor.phone,
    doctor.officeStreet,
    doctor.clinicAddress,
    doctor.officeCity,
    doctor.officeState,
  ]);

  const incomplete = useMemo(() => {
    const missing: string[] = [];
    if (stripMask(cpf).length !== 11) missing.push('CPF');
    if (!dateOfBirth) missing.push('data de nascimento');
    if (sex !== 'M' && sex !== 'F') missing.push('sexo');
    if (stripMask(phone).length < 10) missing.push('telefone');
    if (!officeCity.trim()) missing.push('cidade');
    if (officeState.trim().length !== 2) missing.push('UF');
    return missing;
  }, [cpf, dateOfBirth, sex, phone, officeCity, officeState]);

  const handleSave = async () => {
    const cpfDigits = stripMask(cpf);
    const phoneDigits = stripMask(phone);
    if (cpfDigits && cpfDigits.length !== 11) {
      toast.error('CPF deve conter 11 dígitos.');
      return;
    }
    if (!dateOfBirth) {
      toast.error('Informe a data de nascimento.');
      return;
    }
    if (sex !== 'M' && sex !== 'F') {
      toast.error('Selecione o sexo (M ou F).');
      return;
    }
    if (phoneDigits.length < 10) {
      toast.error('Informe o telefone com DDD.');
      return;
    }
    if (!officeCity.trim()) {
      toast.error('Informe a cidade.');
      return;
    }
    if (officeState.trim().length !== 2) {
      toast.error('Selecione a UF.');
      return;
    }

    try {
      setIsSaving(true);
      const response = await updateDoctor(doctor.id, {
        cpf: cpfDigits,
        dateOfBirth,
        sex,
        phone: phoneDigits,
        officeStreet: officeStreet.trim() || undefined,
        officeCity: officeCity.trim(),
        officeState: officeState.trim().toUpperCase(),
      });

      if (response.success && response.data) {
        toast.success('Dados pessoais atualizados. Você já pode usar a prescrição digital.');
        onUpdate(response.data);
        setCpf(formatCpf(response.data.cpf || cpfDigits) || cpfDigits);
        setDateOfBirth(toDateInputValue(response.data.dateOfBirth) || dateOfBirth);
        setSex((response.data.sex as 'M' | 'F') || sex);
        setPhone(formatPhone(response.data.phone || phoneDigits) || phoneDigits);
        setOfficeStreet(response.data.officeStreet || response.data.clinicAddress || officeStreet);
        setOfficeCity(response.data.officeCity || officeCity);
        setOfficeState(response.data.officeState || officeState);
      } else {
        toast.error(
          typeof response.error === 'string' ? response.error : 'Erro ao atualizar dados pessoais.',
        );
      }
    } catch (error) {
      console.error('Error updating doctor identity:', error);
      toast.error('Erro ao salvar dados pessoais.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border-gray-200 dark:border-neutral-800 shadow-xs">
      <CardHeader className="border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/30">
        <div className="flex items-center gap-2">
          <IdCard className="w-5 h-5 text-teal-600" />
          <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
            Dados para prescrição digital
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {incomplete.length > 0 && (
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-200">
            Complete estes campos para usar a Memed: {incomplete.join(', ')}.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-bold text-gray-700 dark:text-gray-300">CPF *</Label>
            <Input
              value={cpf}
              onChange={(e) => setCpf(formatCpf(e.target.value))}
              placeholder="000.000.000-00"
              inputMode="numeric"
              className="bg-white dark:bg-neutral-900"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-bold text-gray-700 dark:text-gray-300">
              Data de nascimento *
            </Label>
            <Input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="bg-white dark:bg-neutral-900"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-bold text-gray-700 dark:text-gray-300">Sexo *</Label>
            <select
              value={sex}
              onChange={(e) => setSex(e.target.value as 'M' | 'F' | '')}
              className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            >
              <option value="">Selecione</option>
              <option value="M">Masculino (M)</option>
              <option value="F">Feminino (F)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-bold text-gray-700 dark:text-gray-300">Telefone *</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(00) 00000-0000"
              inputMode="tel"
              className="bg-white dark:bg-neutral-900"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-bold text-gray-700 dark:text-gray-300">Endereço</Label>
            <Input
              value={officeStreet}
              onChange={(e) => setOfficeStreet(e.target.value)}
              placeholder="Rua / Avenida"
              className="bg-white dark:bg-neutral-900"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-bold text-gray-700 dark:text-gray-300">Cidade *</Label>
            <Input
              value={officeCity}
              onChange={(e) => setOfficeCity(e.target.value)}
              placeholder="Cidade"
              className="bg-white dark:bg-neutral-900"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-bold text-gray-700 dark:text-gray-300">UF *</Label>
            <select
              value={officeState}
              onChange={(e) => setOfficeState(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            >
              <option value="">Selecione</option>
              {BRAZIL_UF_OPTIONS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </div>
        </div>

        <p className="text-[11px] text-gray-500 italic">
          Exigidos pela Memed no cadastro do prescritor (telefone, cidade/UF e dados pessoais). O
          consultório completo continua na seção de clínica abaixo.
        </p>

        <div className="pt-2">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar dados pessoais
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
